import { SKILL_TEMPLATES } from "./skill-templates/index.js";

function compareSemver(a: string, b: string): number {
  const pa = a.split(".").map((n) => parseInt(n, 10) || 0);
  const pb = b.split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < 3; i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

export function knownVersions(name: string): string[] {
  const t = SKILL_TEMPLATES[name];
  if (!t) return [];
  const all = new Set<string>([t.version, ...Object.keys(t.previousVersions ?? {})]);
  return [...all].sort(compareSemver);
}

function renderAtVersion(name: string, version: string): string | null {
  const t = SKILL_TEMPLATES[name];
  if (!t) return null;
  if (version === t.version) return t.render();
  const prev = t.previousVersions?.[version];
  return prev ? prev() : null;
}

function parseFlagArgs(argv: string[]): {
  positional: string[];
  flags: Record<string, string | true>;
} {
  const positional: string[] = [];
  const flags: Record<string, string | true> = {};
  for (let i = 0; i < argv.length; i++) {
    const a = argv[i];
    if (a.startsWith("--")) {
      const key = a.slice(2);
      const next = argv[i + 1];
      if (next !== undefined && !next.startsWith("--")) {
        flags[key] = next;
        i++;
      } else {
        flags[key] = true;
      }
    } else {
      positional.push(a);
    }
  }
  return { positional, flags };
}

export function runVersions(argv: string[]) {
  const [name] = argv;
  if (!name || !SKILL_TEMPLATES[name]) {
    console.error(`Usage: canary skill versions <name>`);
    process.exit(1);
  }
  const versions = knownVersions(name);
  const current = SKILL_TEMPLATES[name].version;
  for (const v of versions) {
    console.log(v === current ? `${v} (current)` : v);
  }
}

function jsUnifiedDiff(a: string, b: string, labelA: string, labelB: string): string {
  const aLines = a.split("\n");
  const bLines = b.split("\n");
  const n = aLines.length;
  const m = bLines.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    Array.from<number>({ length: m + 1 }).fill(0)
  );
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] =
        aLines[i] === bLines[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out: string[] = [`--- ${labelA}`, `+++ ${labelB}`];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (aLines[i] === bLines[j]) {
      out.push(` ${aLines[i]}`);
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push(`-${aLines[i++]}`);
    } else {
      out.push(`+${bLines[j++]}`);
    }
  }
  while (i < n) out.push(`-${aLines[i++]}`);
  while (j < m) out.push(`+${bLines[j++]}`);
  return out.join("\n") + "\n";
}

export function runDiff(argv: string[]) {
  const { positional, flags } = parseFlagArgs(argv);
  const [name] = positional;

  if (!name) {
    console.error("Usage: canary skill diff <name> [--from <version>] [--to <version>]");
    process.exit(1);
  }

  const template = SKILL_TEMPLATES[name];
  if (!template) {
    console.error(`Unknown skill "${name}".`);
    process.exit(1);
  }

  const versions = knownVersions(name);
  const to = typeof flags.to === "string" ? flags.to : template.version;
  const from =
    typeof flags.from === "string" ? flags.from : versions.filter((v) => v !== to).slice(-1)[0];

  if (!from) {
    console.error(
      `No prior version of "${name}" to diff against. Only known version: ${template.version}.`
    );
    process.exit(1);
  }

  const fromContent = renderAtVersion(name, from);
  const toContent = renderAtVersion(name, to);

  if (fromContent === null) {
    console.error(
      `Version "${from}" of skill "${name}" is not in the CLI. Known: ${versions.join(", ")}.`
    );
    process.exit(1);
  }
  if (toContent === null) {
    console.error(
      `Version "${to}" of skill "${name}" is not in the CLI. Known: ${versions.join(", ")}.`
    );
    process.exit(1);
  }

  if (from === to) {
    console.log(`(no diff — both sides are v${from})`);
    return;
  }

  process.stdout.write(jsUnifiedDiff(fromContent, toContent, `${name}@${from}`, `${name}@${to}`));
}
